import React from 'react';
import { Check, X } from 'lucide-react';

interface QuestionPickRowProps {
  questionNumber: number;
  questionText: string | null;
  optionText: string | null;
  /**
   * The player's free-typed answer, used when there is no option to name — in practice a
   * tiebreaker's number (DKPLAT-732). A tiebreaker joins to no options row, so optionText
   * is always null for one and the row used to read "No selection" even though the answer
   * was recorded and visible in admin.
   */
  customValue?: string | null;
  optionImage: string | null;
  isCorrect: boolean | null;
  pointsEarned: number;
  isCurrentUser?: boolean;
}

export const QuestionPickRow: React.FC<QuestionPickRowProps> = ({
  questionNumber,
  questionText,
  optionText,
  customValue,
  optionImage,
  isCorrect,
  pointsEarned,
  isCurrentUser = false,
}) => {
  // Prefer the named option; fall back to a typed answer. "No selection" is now reserved
  // for a genuinely absent pick, which is the only case it was ever meant to describe.
  const answerText = optionText || customValue || null;
  const baseTextColor = isCurrentUser ? 'text-white' : 'text-text-primary';
  const secondaryTextColor = isCurrentUser ? 'text-white/70' : 'text-text-secondary';

  return (
    <div className="flex items-center gap-3 py-2">
      {/* Question Number */}
      <span
        className={`w-4 text-sm font-medium shrink-0 ${
          isCurrentUser ? 'text-white/70' : 'text-text-secondary'
        }`}
      >
        {questionNumber}
      </span>

      {/* Image */}
      {optionImage && (
        <img
          src={optionImage}
          alt=""
          className="w-8 h-8 object-contain rounded shrink-0"
          onError={e => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}

      {/* Question Type + Answer (stacked) */}
      <div className="flex flex-col flex-1 min-w-0">
        {questionText && (
          <span className={`text-xs truncate ${secondaryTextColor}`}>{questionText}</span>
        )}
        <span className={`truncate ${baseTextColor}`}>{answerText ?? 'No selection'}</span>
      </div>

      {/* Points + Correct/Incorrect */}
      <div className="flex items-center gap-2 shrink-0">
        {isCorrect !== null && (
          <div
            className={`w-5 h-5 rounded-full flex items-center justify-center ${
              isCorrect ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}
          >
            {isCorrect ? (
              <Check className="w-3 h-3 text-green-500" />
            ) : (
              <X className="w-3 h-3 text-red-500" />
            )}
          </div>
        )}
        <span className={`text-sm font-medium ${secondaryTextColor}`}>
          {pointsEarned > 0 ? `+${pointsEarned}` : pointsEarned}
        </span>
      </div>
    </div>
  );
};
